/**
 * Rekap indisipliner (alpha kelas & tahfidz) satu santri selama satu tahun ajaran: total alpha, jumlah kasus,
 * dan riwayat kasus sebelumnya beserta tindakan dan penindaknya.
 * Dipakai di form tindakan dan tombol Riwayat di halaman Kasus Indisipliner.
 */
import React, { useEffect, useState } from 'react';
import { Clock, History } from 'lucide-react';
import { bacaRekapSantri, RekapSantri } from '../../hooks/useKasusIndisipliner';

/** Deretan foto bukti kecil; ketuk untuk membuka ukuran penuh. */
export const BuktiFotoThumbs: React.FC<{ urls?: string[] | null; className?: string }> = ({ urls, className = '' }) => {
    const daftar = (urls || []).filter(Boolean);
    if (daftar.length === 0) return null;
    return (
        <div className={`flex gap-2 ${className}`}>
            {daftar.map((u, i) => (
                <a key={u} href={u} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 shrink-0" title={`Bukti foto ${i + 1}`}>
                    <img src={u} alt={`Bukti foto ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                </a>
            ))}
        </div>
    );
};

const fmtTgl = (s: string) =>
    s ? new Date(`${s}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

interface Props {
    santriId: string;
    tahunAjaran: string;
    /** Kasus yang sedang dibuka, disembunyikan dari daftar riwayat. */
    kecualiId?: string;
    /** Batasi jumlah baris riwayat (form tindakan); kosong = semua. */
    maksBaris?: number;
}

export const KasusRekapSantri: React.FC<Props> = ({ santriId, tahunAjaran, kecualiId, maksBaris }) => {
    const [rekap, setRekap] = useState<RekapSantri | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [semua, setSemua] = useState(false);

    useEffect(() => {
        let batal = false;
        setRekap(null);
        setError(null);
        bacaRekapSantri(santriId, tahunAjaran)
            .then(r => { if (!batal) setRekap(r); })
            .catch(e => { if (!batal) setError(e?.message || 'Gagal memuat rekap.'); });
        return () => { batal = true; };
    }, [santriId, tahunAjaran]);

    if (error) return <p className="text-xs text-red-600">Rekap santri gagal dimuat: {error}</p>;
    if (!rekap) {
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                <Clock className="w-4 h-4 animate-spin" /> Memuat rekap santri...
            </div>
        );
    }

    const items = rekap.items.filter(i => i.id !== kecualiId);
    const tampil = maksBaris && !semua ? items.slice(0, maksBaris) : items;

    const stat = [
        { label: 'Kasus', nilai: rekap.totalKasus, cls: 'text-gray-900' },
        { label: 'Sudah ditindak', nilai: rekap.ditindak, cls: 'text-green-600' },
        { label: 'Jam alpha kelas', nilai: rekap.alphaKelas, cls: 'text-amber-600' },
        { label: 'Waktu alpha tahfidz', nilai: rekap.alphaTahfidz, cls: 'text-cyan-600' },
    ];

    return (
        <div className="space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <History className="w-4 h-4" /> Rekap Tahun Ajaran {rekap.tahunAjaran}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {stat.map(s => (
                    <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-2 text-center">
                        <p className={`text-lg font-bold ${s.cls}`}>{s.nilai}</p>
                        <p className="text-[10px] text-gray-500 leading-tight">{s.label}</p>
                    </div>
                ))}
            </div>

            {items.length === 0 ? (
                <p className="text-xs text-gray-500">Belum ada riwayat indisipliner lain di tahun ajaran ini.</p>
            ) : (
                <ul className="space-y-2">
                    {tampil.map(i => (
                        <li key={i.id} className="bg-white rounded-xl border border-gray-100 p-3 text-xs">
                            <div className="flex justify-between gap-2 mb-1">
                                <span className="font-bold text-gray-800">
                                    {fmtTgl(i.tanggal)} · Alpha {i.kategori} {i.jenis === 'pekanan' ? '(Pekanan)' : '(Beruntun)'}
                                </span>
                                <span className={`shrink-0 font-bold ${i.status === 'Selesai' ? 'text-green-600' : i.status === 'Dibatalkan' ? 'text-gray-400' : 'text-orange-600'}`}>
                                    {i.status}
                                </span>
                            </div>
                            {i.ringkasan && <p className="text-gray-600">{i.ringkasan}</p>}
                            {i.tindakan && i.status !== 'Dibatalkan' && (
                                <p className="mt-1 text-gray-700">
                                    Tindakan: <b>{i.tindakan}</b>
                                    {i.penindak ? ` oleh ${i.penindak}${i.peran ? ` (${i.peran})` : ''}` : ''}
                                </p>
                            )}
                            {!i.selesai && i.penindak && <p className="mt-1 text-gray-500">Dipegang: {i.penindak}</p>}
                            {i.catatan && <p className="mt-1 text-gray-500 italic">"{i.catatan}"</p>}
                            <BuktiFotoThumbs urls={i.buktiFoto} className="mt-2" />
                        </li>
                    ))}
                </ul>
            )}
            {maksBaris && !semua && items.length > maksBaris && (
                <button onClick={() => setSemua(true)} className="text-xs font-bold text-brand-primary">
                    Lihat semua ({items.length})
                </button>
            )}
        </div>
    );
};
